import { ChevronDown, Layers, Code2, MessageCircle, Wand2, Palette, Megaphone, Briefcase, ArrowUpDown, Bot } from "lucide-react";
import type { FilterCategory, SortOption } from "@/types/tool";
import type { Tool } from "@/types/tool";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface FilterTabsProps {
  activeFilter: FilterCategory;
  onFilterChange: (filter: FilterCategory) => void;
  priceFilter: string;
  onPriceFilterChange: (value: string) => void;
  tools: Tool[];
  sortOption: SortOption;
  onSortChange: (sort: SortOption) => void;
}

const categoryFilters: { id: FilterCategory; label: string; icon: React.ReactNode; highlight?: boolean }[] = [
  { id: "all", label: "All", icon: <Layers className="w-3.5 h-3.5" /> },
  { id: "Agentic", label: "Agentic", icon: <Bot className="w-3.5 h-3.5" />, highlight: true },
  { id: "Vibe Coding", label: "Vibe", icon: <Wand2 className="w-3.5 h-3.5" /> },
  { id: "App Builder", label: "Code", icon: <Code2 className="w-3.5 h-3.5" /> },
  { id: "Chatbot", label: "Chat", icon: <MessageCircle className="w-3.5 h-3.5" /> },
  { id: "Marketing", label: "Mktg", icon: <Megaphone className="w-3.5 h-3.5" /> },
  { id: "Creative", label: "Art", icon: <Palette className="w-3.5 h-3.5" /> },
  { id: "Productivity", label: "Prod", icon: <Briefcase className="w-3.5 h-3.5" /> },
];

const priceOptions = [
  { value: "all", label: "Any Price" },
  { value: "free", label: "Free ($0)" },
  { value: "budget", label: "Budget ($1-20)" },
  { value: "premium", label: "Premium ($20+)" },
];

const sortOptions: { value: SortOption; label: string }[] = [
  { value: "price-asc", label: "Price ↑" },
  { value: "price-desc", label: "Price ↓" },
  { value: "autonomy-desc", label: "Autonomy" },
  { value: "name-asc", label: "A → Z" },
];

const FilterTabs = ({
  activeFilter,
  onFilterChange,
  priceFilter,
  onPriceFilterChange,
  tools,
  sortOption,
  onSortChange,
}: FilterTabsProps) => {
  return (
    <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-hide py-1 -mx-1 px-1">
      {categoryFilters.map((f) => {
        const count = f.id === "all" ? tools.length : tools.filter(t => t.category === f.id).length;
        const active = activeFilter === f.id;
        return (
          <button
            key={f.id}
            onClick={() => onFilterChange(f.id)}
            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11px] font-medium whitespace-nowrap transition-all duration-200 shrink-0 ${
              active
                ? "bg-foreground text-background"
                : f.highlight
                  ? "bg-violet-500/10 text-violet-700 dark:text-violet-300 border border-violet-500/30 hover:bg-violet-500/20"
                  : "bg-secondary/80 text-muted-foreground hover:text-foreground hover:bg-secondary"
            }`}
          >
            {f.icon}
            {f.label}
            {f.highlight && !active && (
              <span className="text-[9px] font-bold uppercase tracking-wide px-1 py-px rounded bg-violet-500 text-white">New</span>
            )}
            <span className={`text-[10px] ${active ? "opacity-60" : "opacity-40"}`}>{count}</span>
          </button>
        );
      })}

      <div className="w-px h-4 bg-border shrink-0 mx-0.5" />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11px] font-medium whitespace-nowrap shrink-0 transition-all duration-200 ${
              priceFilter !== "all"
                ? "bg-foreground/10 text-foreground"
                : "bg-secondary/80 text-muted-foreground hover:text-foreground hover:bg-secondary"
            }`}
          >
            Price
            <ChevronDown className="w-3 h-3" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="bg-card border border-border min-w-[140px] z-50 rounded-xl shadow-lg">
          <DropdownMenuRadioGroup value={priceFilter} onValueChange={onPriceFilterChange}>
            {priceOptions.map((o) => (
              <DropdownMenuRadioItem key={o.value} value={o.value} className="text-xs py-2 rounded-lg">
                {o.label}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11px] font-medium whitespace-nowrap shrink-0 bg-secondary/80 text-muted-foreground hover:text-foreground hover:bg-secondary transition-all duration-200">
            <ArrowUpDown className="w-3 h-3" />
            Sort
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="bg-card border border-border min-w-[130px] z-50 rounded-xl shadow-lg">
          <DropdownMenuRadioGroup value={sortOption} onValueChange={(v) => onSortChange(v as SortOption)}>
            {sortOptions.map((o) => (
              <DropdownMenuRadioItem key={o.value} value={o.value} className="text-xs py-2 rounded-lg">
                {o.label}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default FilterTabs;
